// src/pages/CostSimulator.tsx
import { Header } from "@/components/navigation/Header";
import { HeroSection } from "@/components/cost-simulator/HeroSection";
import { CostSimulatorTool } from "@/components/cost-simulator/CostSimulatorTool";
import { SizingConfigDrawer } from "@/components/cost-simulator/SizingConfigDrawer";
import { useState, useRef, useMemo } from "react";
import { useCostSimulatorSizing } from "@/hooks/useCostSimulatorSizing";
import { useCostSimulatorState } from "@/hooks/useCostSimulatorState";

const CostSimulator = () => {
  const [showDetails, setShowDetails] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const detailsRef = useRef<HTMLDivElement>(null);

  // Get sizing data using the new hook with auto-run
  const sizing = useCostSimulatorSizing();
  const snapshot = sizing.snapshot;

  // Get cost comparison data for hero KPIs
  const { comparison } = useCostSimulatorState(snapshot);

  // Get model display name from catalog
  const modelDisplayName = useMemo(() => {
    if (!snapshot?.modelId) return undefined;
    return snapshot.modelId;
  }, [snapshot?.modelId]);

  const handleRunScenario = () => {
    setShowDetails(true);
    setTimeout(() => {
      detailsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  // Default KPIs from actual calculation if available
  const heroKpis = snapshot ? {
    breakEvenMonth: comparison.breakEvenMonth || undefined,
    fiveYearOnPremTco: comparison.fiveYearOnPremTco || undefined,
    fiveYearCloudSpend: comparison.fiveYearCloudSpend || undefined,
  } : {
    breakEvenMonth: "Month 18",
    fiveYearOnPremTco: 2400000,
    fiveYearCloudSpend: 3100000
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <HeroSection 
        onRunScenario={handleRunScenario}
        defaultKpis={heroKpis}
      />
      
      {/* Sizing Config Drawer */}
      <SizingConfigDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        sizing={sizing}
      />
      
      {/* Main Content */}
      <main className="pb-8 sm:pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div 
              ref={detailsRef}
              className={`transition-all duration-1000 ${
                showDetails 
                  ? 'opacity-100 transform translate-y-0' 
                  : 'opacity-0 transform translate-y-8 pointer-events-none'
              }`}
            >
              <CostSimulatorTool 
                snapshot={snapshot}
                modelDisplayName={modelDisplayName}
                sizing={sizing}
                onOpenDrawer={() => setDrawerOpen(true)}
                onMirrorSizing={sizing.mirrorFromSizingPage}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CostSimulator;
